/**
 * RecordNotFound — what a URL that names no record renders (UI-06).
 *
 * Six modules used to render a blank white screen for `/<entity>/new`: the
 * `<entity>/$id` story route matched the literal segment `new`, fired a detail
 * read for a record whose id was the word "new", got a 422, and painted
 * nothing at all. Two of them fired a second 422 against the graph. A blank
 * page tells the person nothing, and tells the next engineer nothing either.
 *
 * This is the honest surface for that URL — and for any other segment that is
 * not a record id. It says what happened and gives one way out: back to the
 * module's list.
 *
 * It is deliberately NOT a create form. Only some of those modules can create
 * a record at all (expenses and payments have no create endpoint), and a form
 * that cannot save is a worse lie than a blank page, not a better one. Where a
 * create surface IS in scope, the module registers its own static route for it
 * and this never renders there.
 *
 * ## Why it takes no props
 *
 * The first version took an entity label and a list path per module, which
 * read better — "No such account", "Back to accounts". It cost about a
 * kilobyte across six call sites, against a page-weight ceiling with 0.37 kB
 * of headroom that may only go down. Wording that good is not worth a
 * kilobyte of everyone's first paint, so both facts are derived from the URL
 * instead and the page says the same true thing everywhere. Nothing here is
 * per-module, so nothing here is duplicated per module.
 */

import { type ReactElement } from "react";

/**
 * `/acme/accounts/new` and `/acme/taxes/discounts/new` both belong to a list
 * at `/<tenant>/<module>`. Two segments is all the back link needs, and it is
 * right for a bad id as well as for a literal — `/acme/accounts/garbage` goes
 * back to accounts, not to a second address that does not exist.
 */
function listHref(): string {
  if (typeof window === "undefined") return "/";
  const parts = window.location.pathname.split("/").filter(Boolean);
  return parts.length >= 2 ? `/${parts[0]}/${parts[1]}` : "/";
}

export function RecordNotFound(): ReactElement {
  return (
    <div data-testid="record-not-found" className="flex flex-col gap-3 py-12">
      <h1 className="text-[24px] font-medium text-[var(--color-text-strong)]">
        No such record
      </h1>
      <p className="text-sm text-[var(--color-text-soft)]">
        That address does not name a record in this company.
      </p>
      <a
        data-testid="record-not-found-back"
        href={listHref()}
        className="text-sm underline underline-offset-4"
      >
        Back to the list
      </a>
    </div>
  );
}
